<template>
  <q-card
    v-touch-hold.mouse="onAddMultipleTrials"
    v-ripple="targetIsOpen"
    class="col-auto column full-height noselect"
    :class="{
      'bg-primary': isSelected,
      'text-white': isSelected,
      'bg-green-1': hasReachedMinTrials,
      'cursor-pointer': targetIsOpen,
      'text-grey-6': dimmed,
      'focused-target': focused,
    }"
    @click="onSelectTarget(target)"
  >
    <q-card-section
      v-if="hasDiscriminativeStimulus"
      class="bg-primary-variant text-white"
      :class="{ 'q-py-none': tiny, 'q-py-xs':!tiny }"
    >
      <q-badge
        v-if="!!getTrialsPerTarget(target)"
        floating
        :style="previousPromptLevelStyle"
      >
        {{ getTrialsPerTarget(target) }}
      </q-badge>
      {{ discriminativeStimulus }}
    </q-card-section>
    <q-card-section
      class="col row items-center"
      :class="{
        'q-py-xs': (tiny && hasDiscriminativeStimulus),
        'q-py-sm': dense && !tiny,
        'q-pb-none': !dense && !tiny,
      }"
    >
      <q-badge
        v-if="!hasDiscriminativeStimulus && !!getTrialsPerTarget(target)"
        floating
        :style="previousPromptLevelStyle"
      >
        {{ getTrialsPerTarget(target) }}
      </q-badge>
      <span>
        <q-chip
          v-if="dense && !tiny && showPromptLevelFading"
          size="sm"
          :style="getPromptLevelStyle(getCurrentPromptLevel())"
        >
          <q-avatar
            :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
            :color="getTargetWorkflowProperty('color', '')"
            :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
          >
            <q-icon
              :name="getTargetWorkflowProperty('icon', 'help')"
            />
          </q-avatar>
          {{ getCurrentPromptLevelLabelShort() }}
        </q-chip>
        <q-chip
          v-else-if="dense && !tiny && !showPromptLevelFading"
          size="sm"
          :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
          :color="getTargetWorkflowProperty('color', '')"
          :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
          :icon="getTargetWorkflowProperty('icon', 'help')"
          class="chip-no-label"
        />
      </span>
      {{ target.label }}
    </q-card-section>
    <q-card-section
      v-if="!dense"
      class="q-pt-xs"
    >
      <q-chip
        v-if="showPromptLevelFading"
        size="sm"
        :style="getPromptLevelStyle(getCurrentPromptLevel())"
      >
        <q-avatar
          :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
          :color="getTargetWorkflowProperty('color', '')"
          :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
        >
          <q-icon
            :name="getTargetWorkflowProperty('icon', 'help')"
          />
        </q-avatar>
        {{ getCurrentPromptLevelLabel() }}
      </q-chip>
      <q-chip
        v-else
        size="sm"
        :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
        :color="getTargetWorkflowProperty('color', '')"
        :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
        :icon="getTargetWorkflowProperty('icon', 'help')"
      >
        {{ getTargetWorkflowProperty('label', 'Unknown') }}
      </q-chip>
    </q-card-section>
    <q-menu
      v-if="hasMultiplePromptLevels && targetIsOpen"
      :anchor="promptLevelsMenuPosition"
      transition-show="none"
      transition-hide="none"
      transition-duration="0"
      auto-close
      no-refocus
      :max-height="isMobileView ? '40vh' : 'none'"
      :class="showAddBehavior ? 'prompt-levels-menu--detached' : ''"
      @hide="isSelected = false"
    >
      <q-list
        :style="promptLevelsListStyle"
        :class="showAddBehavior ? 'bg-white rounded-borders shadow-2' : ''"
      >
        <q-item
          v-for="promptLevel in visibleProgramPrompts"
          :key="promptLevel.id"
          clickable
          :style="getPromptLevelListItemStyle(promptLevel)"
          class="prompt-level"
          @click="onRecordData(promptLevel)"
        >
          <q-item-section>{{ promptLevel.trialOutcome }}</q-item-section>
        </q-item>
        <q-item
          v-if="targetWorkflowMinTrials > 0"
          class="min-trials-item text-grey-6"
          dense
        >
          <q-item-section>
            {{ $t("run.collectData.textMinTrials", {minTrials: targetWorkflowMinTrials}) }}
          </q-item-section>
        </q-item>
      </q-list>
      <add-behavior-occurrence-button
        v-if="showAddBehavior"
        @add="onAddBehaviorOccurrence"
      />
    </q-menu>
  </q-card>
</template>
<script>
import { defaultPromptLevelColors } from 'boot/globals';
import AddMultipleTrials from 'components/Modals/AddMultipleTrials';
import AddBehaviorOccurrenceButton from 'components/Programs/DataCollection/AddBehaviorOccurrenceButton';
import {
  chain, filter,
  find, get, head, size,
} from 'lodash';
import { useBehaviorOccurrenceRecorder } from 'src/composables/useBehaviorOccurrenceRecorder';
import DataCollection from 'src/mixins/DataCollection';
import Interactions from 'src/mixins/Interactions';
import { getTextColor } from 'src/utils/TargetUtils';

export default {
  name: 'TrialByTrialData',

  components: { AddBehaviorOccurrenceButton },

  mixins: [DataCollection, Interactions],

  props: {

    dimmed: {
      type: Boolean,
      default: false,
    },

  },

  emits: ['record'],

  setup(props) {
    return useBehaviorOccurrenceRecorder(props);
  },

  data() {
    return {
      isSelected: false,
    };
  },

  computed: {

    showPromptLevelFading() {
      return (
        this.target.status === 'acquisition' || this.targetWorkflow?.type === 'acquisition'
      ) && (['MTL', 'MTL-S'].includes(get(this.programPromptLevelTemplate, 'fadingStrategy')));
    }, // showPromptLevelFading()

    showSimpleFading() {
      return get(this.programPromptLevelTemplate, 'simpleFading');
    },

    isOnMaxPromptLevel() {
      return get(this.getCurrentPromptLevel(), 'id') === chain(this.programPrompts)
        .reject({ excludeFromPromptLevelFading: true })
        .last()
        .get('id')
        .value();
    },

    isMobileView() {
      return (this.$q.platform.is.mobile || this.$q.screen.lt.sm) && !this.$q.platform.is.ipad;
    }, // isMobileView()

    // The detached menu no longer clips, so the list takes over the mobile height constraint.
    promptLevelsListStyle() {
      const style = { minWidth: '100px' };

      if (this.showAddBehavior && this.isMobileView) {
        style.maxHeight = '40vh';
        style.overflowY = 'auto';
      }

      return style;
    }, // promptLevelsListStyle()

    promptLevelsMenuPosition() {
      if (this.$q.platform.is.ipad) {
        return 'bottom left';
      }

      return this.$q.screen.gt.sm ? 'top right' : undefined;
    }, // promptLevelsMenuPosition()

    visibleProgramPrompts() {
      if (!this.showPromptLevelFading || !this.showSimpleFading) {
        return this.programPrompts;
      }

      const negativePromptLevel = chain(this.programPrompts)
        .find((promptLevel) => promptLevel.isNegativeFadeResponse)
        .defaultTo({ id: null, trialOutcome: '-', color: defaultPromptLevelColors.default })
        .value();

      const excludeFromPromptLevelFading = filter(
        this.programPrompts,
        (promptLevel) => promptLevel.excludeFromPromptLevelFading && !promptLevel.isNegativeFadeResponse,
      );

      return chain(this.programPrompts)
        .filter({ id: this.target.currentPromptLevel })
        .concat(negativePromptLevel)
        .concat(
          ...excludeFromPromptLevelFading,
        )
        .value();
    },

    isFrequencyTarget() {
      return this.target.dataType === 'frequency';
    }, // isFrequencyTarget()

    shouldFillZeroFrequency() {
      return this.isFrequencyTarget && get(this.target, 'fillZeroFrequency', false);
    }, // shouldFillZeroFrequency()

    hasRecordedTrials() {
      return chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .filter('trial')
        .size()
        .value() > 0;
    }, // hasRecordedTrials()

    // Checks if a zero-data placeholder ({ target }) already exists in session data
    // to prevent duplicate rows on re-finishing the session.
    hasPlaceholderRow() {
      return !chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .reject('trial')
        .isEmpty()
        .value();
    }, // hasPlaceholderRow()
  },

  methods: {
    getTextColor,

    onAddMultipleTrials() {
      if (!this.targetIsOpen) {
        return;
      }

      this.openComponentDialog(AddMultipleTrials, {
        program: this.sessionProgram,
        promptLevelTemplate: { promptLevels: this.programPrompts },
        showAddBehavior: this.showAddBehavior,
        targetLabel: get(this.target, 'label', ''),
      }, ({ data, behaviorOccurrences }) => {
        // record data of each active promptLevel
        data.forEach((activePromptLevel) => {
          const { promptLevel, value } = activePromptLevel;

          if (value > 0) {
            this.onEditSessionProgramTargetValue(promptLevel, value);
          }
        });
        this.recordOccurrences(behaviorOccurrences);
      });
    }, // onAddMUltipleTrials()

    onSelectTarget() {
      if (!this.targetIsOpen) {
        return;
      }

      this.notifyDeletedPromptLevelTemplate();

      if (!this.hasMultiplePromptLevels) {
        // Only one prompt level - record right away
        this.onRecordData(head(this.programPrompts));
        this.isSelected = false;
        return;
      }

      this.isSelected = true;
    }, // onSelectTarget()

    onRecordData(promptLevel) {
      if (!this.targetIsOpen) {
        return;
      }

      this.isSelected = false;
      const data = {
        promptLevel: promptLevel?.id ?? null,
        scoreValue: promptLevel?.score ?? null,
      };
      if (
        this.showPromptLevelFading
        && this.showSimpleFading
        && (data.promptLevel === null || promptLevel?.isNegativeFadeResponse)) {
        const failPromptLevel = this.getFailPromptLevel();
        data.errorPromptLevel = get(failPromptLevel, 'id', null);
      }

      if (this.promptLevelTemplate !== this.sessionProgram.program.promptLevelTemplate) {
        Object.assign(data, { promptLevelTemplate: this.promptLevelTemplate });
      }

      this.$emit('record', data);
    }, // onRecordData()

    getPromptLevelListItemStyle(promptLevel) {
      if (this.showPromptLevelFading && (get(this.getCurrentPromptLevel(), 'id', '?') === get(promptLevel, 'id', ''))) {
        return `${this.getPromptLevelStyle(promptLevel)}; font-weight:bold;`;
      }

      return this.getPromptLevelStyle(promptLevel);
    }, // getPromptLevelListItemStyle

    getFailPromptLevel() {
      const currentOrder = chain(this.programPrompts)
        .find({ id: this.target.currentPromptLevel })
        .get('order')
        .value();

      return chain(this.programPrompts)
        .reject((promptLevel) => (promptLevel.order <= currentOrder))
        .reject({ excludeFromPromptLevelFading: true })
        .head()
        .defaultTo(null)
        .value();
    }, // getCurrentPromptLevel()

    getCurrentPromptLevel() {
      return find(this.programPrompts, { id: this.target.currentPromptLevel })
        || find(get(this.programPromptLevelTemplate, 'promptLevels', []), { id: this.target.currentPromptLevel });
    }, // getCurrentPromptLevel()

    getCurrentPromptLevelLabel() {
      return get(this.getCurrentPromptLevel(), 'trialOutcome', '?');
    }, // getCurrentPromptLevelLabel()

    getCurrentPromptLevelLabelShort() {
      return this.getCurrentPromptLevelLabel().substring(0, 1);
    }, // getCurrentPromptLevelLabelShort()

    getTrialsPerTarget() {
      const trials = chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .filter('trial')
        .value();

      const trialCount = size(trials);

      if (trialCount === 0) {
        if (!this.hasMultiplePromptLevels) {
          return '';
        }

        const previousPromptLevelId = get(this.target, 'previousPromptLevel');
        if (previousPromptLevelId) {
          return ' ';
        }
        return '';
      }

      if (!this.hasMultiplePromptLevels) {
        return trialCount;
      }

      return this.calculateTrialsSummary(trials);
    }, // getTrialsPerTarget()

    onEditSessionProgramTargetValue(promptLevel, numberOfTrials) {
      const selected = promptLevel || head(this.programPrompts);

      for (let i = 0; i < numberOfTrials; i += 1) {
        this.onRecordData(selected);
      }
    }, // onEditSessionProgramTargetValue()

    finishSession() {
      if (!this.shouldFillZeroFrequency
        || !this.targetIsOpen
        || this.hasRecordedTrials
        || this.hasPlaceholderRow) {
        return;
      }
      return [{
        phantomTrial: true,
        target: this.target.id,
      }];
    }, // finishSession()
  },

};
</script>
<style scoped>

.prompt-level {
  border-width: 0 5px;
  border-style: solid;
}

.min-trials-item {
  border-top: 1px solid var(--q-color-grey-3);
  font-size: 0.8em;
}

</style>
